import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaDownload, FaSpinner, FaEye } from 'react-icons/fa';
import dayjs from 'dayjs';

const API_URL = '/api';

const LocationViewModal = ({ open, onClose, location }) => {
  const [locationDetails, setLocationDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locationTypes, setLocationTypes] = useState([]);
  const [parentLocation, setParentLocation] = useState(null);
  const [loadingFile, setLoadingFile] = useState(null);

  const fetchLocationDetails = useCallback(async () => {
    if (!location?.LocationID) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/locations/${location.LocationID}`);
      if (response.ok) {
        const data = await response.json();
        setLocationDetails(data);
        
        // Fetch parent location if exists
        if (data.ParentLocationID) {
          const parentResponse = await fetch(`${API_URL}/locations/${data.ParentLocationID}`);
          if (parentResponse.ok) {
            const parentData = await parentResponse.json();
            setParentLocation(parentData);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching location details:', error);
    } finally {
      setLoading(false);
    }
  }, [location?.LocationID]);

  useEffect(() => {
    if (open && location) {
      fetchLocationDetails();
      fetchLocationTypes();
    }
  }, [open, location, fetchLocationDetails]);

  const fetchLocationTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/location-types`);
      if (response.ok) {
        const data = await response.json();
        setLocationTypes(data.locationTypes || []);
      }
    } catch (error) {
      console.error('Error fetching location types:', error);
    }
  };

  const getLocationTypeName = (typeId) => {
    const type = locationTypes.find(t => t.LocationTypeID === typeId);
    return type ? type.LocationTypeName : 'Unknown';
  };

  const handleFileDownload = async () => {
    if (!locationDetails?.FileName) return;
    
    try {
      setLoadingFile(true);
      const response = await fetch(`${API_URL}/locations/${locationDetails.LocationID}/files/attachment`);
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = locationDetails.FileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert(`Failed to download file: ${error.message}`);
    } finally {
      setLoadingFile(false);
    }
  };

  const handleFileView = async () => {
    if (!locationDetails?.FileName) return;
    
    try {
      setLoadingFile(true);
      const response = await fetch(`${API_URL}/locations/${locationDetails.LocationID}/files/attachment`);
      if (!response.ok) {
        throw new Error('Failed to view file');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing file:', error);
      alert(`Failed to view file: ${error.message}`);
    } finally {
      setLoadingFile(false);
    }
  };

  const renderFilePreview = () => {
    if (!locationDetails?.FileName) return '-';

    const isLoading = loadingFile;
    const isImage = locationDetails.FileType?.startsWith('image/');

    return (
      <div className="flex items-center gap-2">
        {isImage ? (
          <img 
            src={`${API_URL}/locations/${locationDetails.LocationID}/files/attachment`} 
            alt="Location File" 
            className="w-10 h-10 object-cover rounded"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM2NjY2NjYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHJ4PSIyIiByeT0iMiI+PC9yZWN0PjxjaXJjbGUgY3g9IjguNSIgY3k9IjguNSIgcj0iMS41Ij48L2NpcmNsZT48cG9seWxpbmUgcG9pbnRzPSIyMSAxNSAxNSAxNSA5IDkgMyAxNSI+PC9wb2x5bGluZT48L3N2Zz4=';
            }}
          />
        ) : (
          <FaDownload className="text-[#2b6777] text-xl" />
        )}
        <button
          onClick={handleFileView}
          className="p-2 text-[#2b6777] hover:text-[#52ab98] disabled:opacity-50"
          disabled={isLoading}
          title="View"
        >
          <FaEye />
        </button>
        <button
          onClick={handleFileDownload}
          className="p-2 text-[#2b6777] hover:text-[#52ab98] disabled:opacity-50"
          disabled={isLoading}
          title="Download"
        >
          <FaDownload />
        </button>
        {isLoading && <span className="text-sm text-gray-500">Loading...</span>}
      </div>
    );
  };

  if (!open || !location) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex items-center gap-4">
            <FaSpinner className="animate-spin text-[#2b6777]" />
            <span className="text-[#2b6777]">Loading location details...</span>
          </div>
        </div>
      </div>
    );
  }

  const sections = [
    {
      title: 'Location Details',
      fields: [
        { label: 'Location Code', value: locationDetails?.LocationCode },
        { label: 'Location Name', value: locationDetails?.LocationName },
        { label: 'Location Type', value: getLocationTypeName(locationDetails?.LocationTypeID) },
        { label: 'Parent Location', value: parentLocation ? `${parentLocation.LocationName} (${parentLocation.LocationCode})` : 'None' },
      ]
    },
    {
      title: 'Address Information',
      fields: [
        { label: 'Country', value: locationDetails?.Country || '-' },
        { label: 'State/Province', value: locationDetails?.State || '-' },
        { label: 'City', value: locationDetails?.City || '-' },
        { label: 'Postal/Zip Code', value: locationDetails?.PostalCode || '-' },
        { label: 'Address Line 1', value: locationDetails?.AddressLine1 || '-' },
        { label: 'Address Line 2', value: locationDetails?.AddressLine2 || '-' },
      ]
    },
    {
      title: 'Documents',
      fields: [
        {
          label: 'Address Proof Document',
          value: renderFilePreview()
        }
      ]
    },
    {
      title: 'System Information',
      fields: [
        { 
          label: 'Status', 
          value: locationDetails?.Status ? 'Active' : 'Inactive',
          isStatus: true
        },
        { 
          label: 'Created At', 
          value: locationDetails?.CreatedAt ? dayjs(locationDetails.CreatedAt).format('YYYY-MM-DD HH:mm') : '-' 
        },
        { 
          label: 'Updated At', 
          value: locationDetails?.UpdatedAt ? dayjs(locationDetails.UpdatedAt).format('YYYY-MM-DD HH:mm') : '-' 
        },
        { label: 'Created By', value: locationDetails?.CreatedBy || '-' },
        { label: 'Updated By', value: locationDetails?.UpdatedBy || '-' },
      ]
    }
  ];

 return (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
    <div className="bg-[#E5E1DA] rounded-2xl shadow-2xl w-full max-w-4xl p-4 sm:p-6 md:p-8 relative overflow-y-auto max-h-[90vh]">
      
      {/* Close Button */}
      <button 
        className="absolute top-3 right-3 text-[#2b6777] hover:text-[#52ab98]" 
        onClick={onClose}
      >
        <FaTimes size={20} />
      </button>
      
      {/* Title */}
      <h2 className="text-xl sm:text-2xl font-bold text-[#2b6777] mb-4 sm:mb-6 text-center">
        Location Details
      </h2>

      {/* Sections */}
      <div className="space-y-4 sm:space-y-6">
        {sections.map((section, index) => (
          <div key={index} className="bg-white rounded-xl p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-bold text-[#2b6777] mb-3 sm:mb-4">
              {section.title}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {section.fields.map((field, fieldIndex) => (
                <div key={fieldIndex} className="flex flex-col">
                  <span className="text-xs sm:text-sm font-medium text-gray-600">
                    {field.label}
                  </span>
                  <span className="text-sm sm:text-base text-gray-900 mt-1">
                    {field.isStatus ? (
                      <span
                        className={`inline-block px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold shadow-sm
                          ${field.value === 'Active'
                            ? 'bg-[#52ab98] text-white'
                            : 'bg-gray-300 text-gray-500'}`}
                      >
                        {field.value}
                      </span>
                    ) : (
                      field.value
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="mt-4 sm:mt-6 flex justify-center sm:justify-end">
        <button
          className="px-4 py-2 rounded bg-[#52ab98] hover:bg-[#2b6777] text-white font-semibold shadow transition text-sm sm:text-base"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

};

export default LocationViewModal; 